<template>
  <Modal name="set-password" :custom="true" @before-open="handleOpen">
    <div :class="modalClass">
      <CloseIcon width="24px" height="24px" class="set-password-modal__close" @click="$modal.hide()" />
      <div class="set-password-modal__title">Set password</div>
      <form class="set-password-modal__form" @submit.prevent="handleReset">
        <BravadoTheme forced-theme="light">
          <BravadoTextInputV3
            v-model="form.password"
            :is-valid="isFieldValid('password')"
            :hint="passwordErrorMessage"
            label="New password"
            type="password"
            name="password"
            autocomplete="new-password"
            @blur="$v.form.password.$touch"
          />
          <BravadoTextInputV3
            v-model="form.passwordConfirmation"
            :is-valid="isFieldValid('passwordConfirmation')"
            :hint="passwordConfirmationErrorMessage"
            label="Confirm your password"
            type="password"
            name="passwordConfirmation"
            class="set-password-modal__field"
            autocomplete="new-password"
            @blur="$v.form.passwordConfirmation.$touch"
          />
        </BravadoTheme>

        <BravadoButtonV3 variant="secondary" size="md" :loading="loading" full-width class="set-password-modal__field">
          Set New Password
        </BravadoButtonV3>
      </form>
    </div>
  </Modal>
</template>

<script>
import { validationMixin } from 'vuelidate';
import { minLength, required, sameAs } from 'vuelidate/lib/validators';
import Modal from '~/components/common/Modal/Modal';
import CloseIcon from '~/assets/svg/close.vue.svg';
import BravadoTheme from '~/components/common/BravadoTheme/BravadoTheme.vue';
import BravadoButtonV3 from '~/components/common/BravadoButtonV3/BravadoButtonV3';
import BravadoTextInputV3 from '~/components/common/BravadoTextInputV3/BravadoTextInputV3';

export default {
  name: 'SetPasswordModal',
  components: {
    Modal,
    CloseIcon,
    BravadoTheme,
    BravadoButtonV3,
    BravadoTextInputV3,
  },
  mixins: [validationMixin],
  data() {
    return {
      form: {
        password: '',
        passwordConfirmation: '',
      },
      errors: {},
      isValid: true,
      attemptedSubmission: false,
      loading: false,
      token: null,
    };
  },
  validations: {
    form: {
      password: { required, minLength: minLength(6) },
      passwordConfirmation: { sameAsPassword: sameAs('password') },
    },
  },
  computed: {
    modalClass() {
      return {
        'set-password-modal': true,
        'set-password-modal--failed': !this.isValid,
      };
    },
    passwordErrorMessage() {
      if (this.isFieldValid('password')) return '';
      if (!this.$v.form.password.required) return 'Password is required';
      if (!this.$v.form.password.minLength) return 'Password must be at least 6 characters long';
      if (this.errors.password) return this.errors.password;

      return '';
    },
    passwordConfirmationErrorMessage() {
      if (this.isFieldValid('passwordConfirmation')) return '';
      if (!this.$v.form.passwordConfirmation.sameAsPassword) return 'Password confirmation should match password';
      if (this.errors.password_confirmation) return this.errors.password_confirmation;

      return '';
    },
    resetTokenErrorMessage() {
      return this.errors.reset_password_token || '';
    },
  },
  methods: {
    handleOpen({ params: { reset_password_token: token } }) {
      if (!token) {
        this.$router.push('/');
      }
      this.token = token;
    },
    isFieldValid(fieldName) {
      const formElement = this.$v.form[fieldName];
      return !(this.attemptedSubmission && formElement.$invalid) && this.isValid && !this.errors[fieldName];
    },
    async handleReset() {
      this.errors = {};

      if (this.$v.form.$invalid) {
        this.attemptedSubmission = true;
      } else {
        try {
          this.loading = true;
          await this.$axios.$put('/users/password.json', {
            user: {
              password: this.form.password,
              password_confirmation: this.form.passwordConfirmation,
              reset_password_token: this.token,
            },
          });
          await this.$auth.fetchUser();
          this.$router.push(this.$pages.WAR_ROOM_POSTS_TAB_MYFEED_PAGE.path);
        } catch (e) {
          this.isValid = false;

          if (e.response?.data?.errors) {
            this.errors = e.response.data.errors;

            if (this.errors.reset_password_token) {
              this.$notice.error(this.errors.reset_password_token, e);
            }
          } else {
            this.$notice.error('Something went wrong. Please try again later.', e);
          }

          setTimeout(() => {
            this.isValid = true;
          }, 3000);
        } finally {
          this.loading = false;
          this.$modal.hide('set-password');
        }
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '~/assets/styles/uikit/utils/transition.scss';

.set-password-modal {
  $this: &;

  padding: 18px 24px 28px;
  background: var(--universal-color-white);
  width: 100%;
  min-height: 100vh;
  position: relative;

  @include mq('sm') {
    min-height: auto;
    max-width: 375px;
    box-shadow: 0 16px 16px rgba(var(--universal-color-black-rgb), 0.08),
      0 8px 16px rgba(var(--universal-color-black-rgb), 0.04);
    border-radius: 16px;
  }

  &--failed {
    @extend %shake-transition;
  }

  &__close {
    position: absolute;
    top: 17px;
    right: 16px;
    cursor: pointer;
    z-index: 2;
    color: rgba(var(--universal-color-black-rgb), 0.3);

    &:hover {
      color: var(--universal-color-black);
    }
  }

  &__title {
    @include body-semibold;

    color: var(--universal-color-gray-70);
  }

  &__field {
    margin-top: 24px;
  }

  &__form {
    margin-top: 21px;
  }
}
</style>
